import { AddCircle } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { DeleteButton, SaveButton } from '~/components/button';
import { AddPropertyGroupDialog } from '~/components/dialogs/add-property-group';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { FormLayout } from '~/components/settings';
import { useAppContext } from '~/contexts';
import type {
  DeviceSettingsPage__Device,
  DeviceSettingsPage__PropertyGroup,
} from '../../queries/settings.generated';
import { ActionBar, FormHeading, useSettingsForm } from '../lib';

export interface SettingsFormProps {
  device: DeviceSettingsPage__Device;
  propertyGroups: readonly DeviceSettingsPage__PropertyGroup[];
}

export const SettingsForm = ({ device, propertyGroups }: SettingsFormProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const canManagePropertyGroups = currentNetwork.canManagePropertyGroups.value;

  const {
    addPropertyGroup,
    confirmDialogProps,
    deletePropertyGroup,
    filteredPropertyGroups,
    formik,
    generalSettings,
    propertyGroupSettings,
    remoteTargetSettings,
    setShowAddPropertyDialog,
    showAddPropertyDialog,
  } = useSettingsForm({ device, propertyGroups });

  if (!device.canUpdate.value) return <Navigate to="/not-found" replace />;

  return (
    <>
      <form onSubmit={formik.handleSubmit} noValidate>
        <ActionBar>
          {canManagePropertyGroups && (
            <Button
              disabled={!filteredPropertyGroups.length}
              onClick={() => setShowAddPropertyDialog(true)}
              startIcon={<AddCircle />}
              variant="contained"
            >
              {t('pages.devices.show.settings.add_property_group')}
            </Button>
          )}
          <SaveButton disabled={!formik.isValid || formik.isSubmitting} type="submit">
            {t('common.actions.save')}
          </SaveButton>
        </ActionBar>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <FormLayout formHeading={t('common.labels.general')} formData={generalSettings} />

          {currentNetwork.canManage.value && formik.values.remoteTargetIds && (
            <FormLayout
              formHeading={
                <FormHeading>
                  <Box>{t('pages.devices.show.settings.remote_targets_heading')}</Box>
                  <DeleteButton
                    onClick={() => formik.setFieldValue('remoteTargetIds', null, true)}
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  >
                    {t('pages.devices.show.settings.remove_group')}
                  </DeleteButton>
                </FormHeading>
              }
              formData={remoteTargetSettings}
            />
          )}

          {currentNetwork.canManage.value &&
            propertyGroupSettings.map((settings) => (
              <FormLayout
                formHeading={
                  <FormHeading>
                    <Box>{settings.name}</Box>
                    {canManagePropertyGroups && (
                      <DeleteButton
                        onClick={() =>
                          deletePropertyGroup({ propertyGroupId: settings.propertyGroupId })
                        }
                        size="small"
                        sx={{ fontSize: '0.7rem' }}
                      >
                        {t('pages.devices.show.settings.remove_group')}
                      </DeleteButton>
                    )}
                  </FormHeading>
                }
                formData={settings.fields}
                key={settings.id}
              />
            ))}
        </Box>
      </form>
      <ConfirmDialog
        {...confirmDialogProps}
        title={t('pages.devices.show.settings.delete_property_group')}
        confirm={t('pages.devices.show.settings.permanently_delete')}
        prompt={t('pages.devices.show.settings.delete_prompt')}
        deleteConfirm
      />
      <AddPropertyGroupDialog
        description={t('pages.devices.show.settings.add_group_description')}
        onSubmit={addPropertyGroup}
        open={showAddPropertyDialog}
        onClose={() => {
          setShowAddPropertyDialog(false);
        }}
        propertyGroups={filteredPropertyGroups}
      />
    </>
  );
};
